import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";

const inputCls = "w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500";
const ROLE_LABEL = { admin: "Admin", manager: "Manager", sales: "Sales" };
const ROLE_COLOR = {
  admin: "bg-violet-50 text-violet-700 border-violet-200",
  manager: "bg-blue-50 text-blue-700 border-blue-200",
  sales: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function Staff() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "sales", password: "", target_monthly: 0 });
  const [editId, setEditId] = useState(null);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    const { data } = await api.get("/staff");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", email: "", role: "sales", password: "", target_monthly: 0 }); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ name: s.name, email: s.email, role: s.role, password: "", target_monthly: s.target_monthly || 0 }); setEditId(s.id); setOpen(true); };

  const save = async () => {
    try {
      if (editId) {
        const payload = { name: form.name, role: form.role, target_monthly: +form.target_monthly || 0 };
        if (form.password) payload.password = form.password;
        await api.put(`/staff/${editId}`, payload);
        toast.success("Staff updated");
      } else {
        if (!form.password) return toast.error("Password required");
        await api.post("/staff", form);
        toast.success("Staff added");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this staff member?")) return;
    try { await api.delete(`/staff/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Staff</h1>
          <p className="text-sm text-slate-500 mt-1">{rows.length} team members {!isAdmin && "· Admin-only actions"}</p>
        </div>
        {isAdmin && (
          <button onClick={openNew} data-testid="staff-add-button" className="h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Staff
          </button>
        )}
      </div>

      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left py-3 pl-4">Name</th>
              <th className="text-left">Email</th>
              <th className="text-left">Role</th>
              <th className="text-right">Monthly Target</th>
              {isAdmin && <th className="pr-4"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} data-testid={`staff-row-${s.id}`} className="border-b border-slate-100 row-hover">
                <td className="py-3 pl-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-700">{(s.name || "?").slice(0, 2).toUpperCase()}</div>
                    <div>
                      <div className="font-semibold text-slate-900">{s.name}</div>
                      {s.id === user?.id && <div className="text-[10px] text-blue-600 font-semibold flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> You</div>}
                    </div>
                  </div>
                </td>
                <td className="text-slate-700 font-mono text-xs">{s.email}</td>
                <td><span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded border ${ROLE_COLOR[s.role]}`}>{ROLE_LABEL[s.role]}</span></td>
                <td className="text-right font-mono">${(s.target_monthly || 0).toLocaleString()}</td>
                {isAdmin && (
                  <td className="pr-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(s)} className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"><Pencil className="w-4 h-4" /></button>
                      {s.id !== user?.id && <button onClick={() => remove(s.id)} className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{editId ? "Edit staff" : "Add staff"}</h3>
            <div><label className="text-xs font-semibold uppercase text-slate-600">Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls + " mt-1"} /></div>
            <div><label className="text-xs font-semibold uppercase text-slate-600">Email</label><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editId} className={inputCls + " mt-1 font-mono disabled:bg-slate-100"} /></div>
            <div><label className="text-xs font-semibold uppercase text-slate-600">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls + " mt-1"}>
                <option value="admin">Admin</option><option value="manager">Manager</option><option value="sales">Sales</option>
              </select>
            </div>
            <div><label className="text-xs font-semibold uppercase text-slate-600">{editId ? "New password (leave blank to keep)" : "Password"}</label><input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls + " mt-1"} /></div>
            <div><label className="text-xs font-semibold uppercase text-slate-600">Monthly target (NZD)</label><input type="number" value={form.target_monthly} onChange={(e) => setForm({ ...form, target_monthly: e.target.value })} className={inputCls + " mt-1 font-mono"} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setOpen(false)} className="h-10 px-4 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold">Cancel</button>
              <button onClick={save} data-testid="staff-save-button" className="h-10 px-4 bg-blue-600 text-white rounded-lg text-sm font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
